import React, { useState, useEffect, useMemo } from 'react';
import { useAuth } from '../../../contexts/AuthContext';
import { useBetaData } from '../../../contexts/BetaDataContext';
import {
  createDeliverable,
  updateDeliverable,
  deleteDeliverable,
  createDeliverableItem,
  updateDeliverableItem,
  deleteDeliverableItem,
  batchUpdateItemAssignee,
  batchUpdateItemRoleType,
  handoffDeliverable,
} from '../../../api/deliverables';
import { useConfirm } from '../../common/ConfirmDialog';
import DeliverableSidebar from './DeliverableSidebar';
import DeliverableContent from './DeliverableContent';
import RoleItemPanel from './RoleItemPanel';
import './DeliverableList.scss';

const DeliverableList = ({ phase, projectId, onDeliverablesChange }) => {
  const { profile, viewingCompanyId } = useAuth();
  const { companyUsers } = useBetaData();
  const { confirm } = useConfirm();

  // ─── Tab state（初始選第一個）───────────────
  const initDeliverables = phase.deliverables || [];
  const [activeTabId, setActiveTabId] = useState(() =>
    initDeliverables.length > 0 ? initDeliverables[0].id : null
  );

  // ─── Panel state ─────────────────────────
  const [panelRole, setPanelRole] = useState(null);

  // ─── Local state = 唯一真實來源 ────────────
  const [localDeliverables, setLocalDeliverables] = useState(initDeliverables);

  // 通知 PhaseItem 即時更新 header 狀態（QA 顏色、進度等）
  useEffect(() => {
    onDeliverablesChange?.(localDeliverables);
  }, [localDeliverables]);

  const companyId = viewingCompanyId || profile?.company_id;
  const deliverables = localDeliverables;

  const usersMap = useMemo(() => {
    const map = {};
    (companyUsers || []).forEach((u) => { if (u.id) map[u.id] = u; });
    return map;
  }, [companyUsers]);


  // ─── Optimistic helpers ─────────────────────

  const updateLocalItem = (itemId, updates) => {
    setLocalDeliverables((prev) =>
      prev.map((d) => ({
        ...d,
        deliverable_items: (d.deliverable_items || []).map((i) =>
          i.id === itemId ? { ...i, ...updates } : i
        ),
      }))
    );
  };

  const updateLocalDeliverable = (deliverableId, updates) => {
    setLocalDeliverables((prev) =>
      prev.map((d) => (d.id === deliverableId ? { ...d, ...updates } : d))
    );
  };

  // ─── Deliverable Container ─────────────────

  const handleAddDeliverable = async (title) => {
    const { data, error } = await createDeliverable({
      phase_id: phase.id,
      company_id: companyId,
      title,
      sort_order: deliverables.length,
    });
    if (!error && data) {
      const newD = { ...data, deliverable_items: data.deliverable_items || [] };
      setLocalDeliverables((prev) => [...prev, newD]);
      setActiveTabId(newD.id);
    }
  };

  const handleDeleteDeliverable = async (id) => {
    const d = deliverables.find((x) => x.id === id);
    const confirmed = await confirm({
      title: '刪除交付項目',
      message: `確定要刪除「${d?.title || ''}」嗎？底下的所有角色和工作項目也會一併刪除。`,
      confirmText: '刪除',
      type: 'danger',
    });
    if (!confirmed) return;
    if (panelRole?.deliverableId === id) setPanelRole(null);
    // Optimistic delete + 選下一個
    const remaining = deliverables.filter((x) => x.id !== id);
    setLocalDeliverables(remaining);
    if (activeTabId === id) {
      setActiveTabId(remaining.length > 0 ? remaining[0].id : null);
    }
    deleteDeliverable(id);
  };

  const handleEditTitleSave = async (d, trimmed) => {
    updateLocalDeliverable(d.id, { title: trimmed });
    updateDeliverable(d.id, { title: trimmed });
  };

  // 上/下移交付項目：與相鄰一筆交換，再依新陣列順序重編 sort_order
  // （順手修正歷史重複/跳號，只把實際變動的筆寫回 DB）
  const handleMoveDeliverable = (id, direction) => {
    const index = deliverables.findIndex((d) => d.id === id);
    if (index === -1) return;
    const targetIndex = direction === 'up' ? index - 1 : index + 1;
    if (targetIndex < 0 || targetIndex >= deliverables.length) return;

    const reordered = [...deliverables];
    [reordered[index], reordered[targetIndex]] = [reordered[targetIndex], reordered[index]];

    const changed = [];
    const next = reordered.map((d, i) => {
      if (d.sort_order !== i) changed.push({ id: d.id, sort_order: i });
      return { ...d, sort_order: i };
    });

    setLocalDeliverables(next);
    changed.forEach(({ id: cid, sort_order }) => updateDeliverable(cid, { sort_order }));
  };

  const handleDescriptionChange = async (deliverableId, description) => {
    updateLocalDeliverable(deliverableId, { description });
    updateDeliverable(deliverableId, { description });
  };

  const handleQaToggle = async (d, status) => {
    const newStatus = d.qa_status === status ? null : status;
    const qaUpdates = {
      qa_status: newStatus,
      qa_verified_by: newStatus ? profile?.id : null,
      qa_verified_at: newStatus ? new Date().toISOString() : null,
    };
    updateLocalDeliverable(d.id, qaUpdates);
    updateDeliverable(d.id, qaUpdates);
  };

  // ─── 交棒 ──────────────────────────────────

  const handleHandoff = async (deliverableId, targetUserId, targetEmail) => {
    const d = deliverables.find((x) => x.id === deliverableId);
    const phaseInfo = phase;

    const handoffUpdates = {
      current_assignee_id: targetUserId,
      handed_off_by: profile?.id,
      handed_off_at: new Date().toISOString(),
    };
    updateLocalDeliverable(deliverableId, handoffUpdates);

    handoffDeliverable(deliverableId, targetUserId, profile?.id, {
      projectName: phaseInfo?.projectName || '',
      phaseName: phaseInfo?.name || '',
      deliverableTitle: d?.title || '',
      targetEmail,
      projectId,
    });
  };

  // ─── Role Section ──────────────────────────

  const handleAssigneeChange = async (deliverableId, roleType, assigneeId) => {
    setLocalDeliverables((prev) =>
      prev.map((d) =>
        d.id === deliverableId
          ? {
              ...d,
              deliverable_items: (d.deliverable_items || []).map((i) =>
                i.role_type === roleType ? { ...i, assignee_id: assigneeId || null } : i
              ),
            }
          : d
      )
    );
    batchUpdateItemAssignee(deliverableId, roleType, assigneeId);
  };

  const handleAddRoleSection = async (deliverableId, roleName, assigneeId) => {
    const { data, error } = await createDeliverableItem({
      deliverable_id: deliverableId,
      company_id: companyId,
      role_type: roleName,
      assignee_id: assigneeId,
      title: '待定義',
      sort_order: 0,
    });
    if (!error && data) {
      setLocalDeliverables((prev) =>
        prev.map((d) =>
          d.id === deliverableId
            ? { ...d, deliverable_items: [...(d.deliverable_items || []), data] }
            : d
        )
      );
    }
  };

  const handleRenameRole = async (deliverableId, oldRoleType, newRoleType) => {
    setLocalDeliverables((prev) =>
      prev.map((d) =>
        d.id === deliverableId
          ? {
              ...d,
              deliverable_items: (d.deliverable_items || []).map((i) =>
                i.role_type === oldRoleType ? { ...i, role_type: newRoleType } : i
              ),
            }
          : d
      )
    );
    // Update panel role if it's open
    if (panelRole?.deliverableId === deliverableId && panelRole?.roleType === oldRoleType) {
      setPanelRole({ deliverableId, roleType: newRoleType });
    }
    batchUpdateItemRoleType(deliverableId, oldRoleType, newRoleType);
  };

  const handleDeleteRole = async (deliverableId, roleType) => {
    const d = deliverables.find((x) => x.id === deliverableId);
    const roleItems = (d?.deliverable_items || []).filter((i) => i.role_type === roleType);
    const confirmed = await confirm({
      title: '刪除角色',
      message: `確定要刪除「${roleType}」嗎？底下的 ${roleItems.length} 個工作項目也會一併刪除。`,
      confirmText: '刪除',
      type: 'danger',
    });
    if (!confirmed) return;
    if (panelRole?.deliverableId === deliverableId && panelRole?.roleType === roleType) {
      setPanelRole(null);
    }
    // Optimistic delete items of this role
    setLocalDeliverables((prev) =>
      prev.map((dd) =>
        dd.id === deliverableId
          ? { ...dd, deliverable_items: (dd.deliverable_items || []).filter((i) => i.role_type !== roleType) }
          : dd
      )
    );
    // 如果被刪角色的 assignee 正好是交棒對象，清除交棒狀態
    const deletedAssignee = roleItems[0]?.assignee_id;
    if (deletedAssignee && d?.current_assignee_id === deletedAssignee) {
      updateDeliverable(deliverableId, {
        current_assignee_id: null,
        handed_off_by: null,
        handed_off_at: null,
      });
    }
    Promise.all(roleItems.map((i) => deleteDeliverableItem(i.id)));
  };

  // ─── Items ─────────────────────────────────

  const handleToggleCompleted = async (item) => {
    const newCompleted = !item.is_completed;
    const itemUpdates = {
      is_completed: newCompleted,
      completed_at: newCompleted ? new Date().toISOString() : null,
    };
    updateLocalItem(item.id, itemUpdates);
    updateDeliverableItem(item.id, { is_completed: newCompleted });
  };

  const handleItemTitleBlur = async (item, value) => {
    const trimmed = value.trim();
    if (!trimmed || trimmed === item.title) return;
    updateLocalItem(item.id, { title: trimmed });
    updateDeliverableItem(item.id, { title: trimmed });
  };

  const handleItemHoursBlur = async (item, value) => {
    const hours = parseFloat(value) || 0;
    if (hours === (parseFloat(item.estimated_hours) || 0)) return;
    updateLocalItem(item.id, { estimated_hours: hours });
    updateDeliverableItem(item.id, { estimated_hours: hours });
  };

  const handleItemActualHoursBlur = async (item, value) => {
    const hours = parseFloat(value) || 0;
    if (hours === (parseFloat(item.actual_hours) || 0)) return;
    updateLocalItem(item.id, { actual_hours: hours });
    updateDeliverableItem(item.id, { actual_hours: hours });
  };

  const handleItemNotesBlur = async (item, value) => {
    if (value === (item.notes || '')) return;
    updateLocalItem(item.id, { notes: value });
    updateDeliverableItem(item.id, { notes: value });
  };

  const handleDeleteItem = async (id) => {
    const confirmed = await confirm({
      title: '刪除工作項目',
      message: '確定要刪除這個工作項目嗎？',
      confirmText: '刪除',
      type: 'danger',
    });
    if (!confirmed) return;
    // Optimistic delete
    setLocalDeliverables((prev) =>
      prev.map((d) => ({
        ...d,
        deliverable_items: (d.deliverable_items || []).filter((i) => i.id !== id),
      }))
    );
    deleteDeliverableItem(id);
  };

  const handleAddItem = async (deliverableId, roleType, assigneeId, title) => {
    const items = deliverables
      .find((d) => d.id === deliverableId)
      ?.deliverable_items?.filter((i) => i.role_type === roleType) || [];
    const { data, error } = await createDeliverableItem({
      deliverable_id: deliverableId,
      company_id: companyId,
      role_type: roleType,
      assignee_id: assigneeId || null,
      title,
      sort_order: items.length,
    });
    if (!error && data) {
      setLocalDeliverables((prev) =>
        prev.map((d) =>
          d.id === deliverableId
            ? { ...d, deliverable_items: [...(d.deliverable_items || []), data] }
            : d
        )
      );
    }
  };

  // ─── Helpers ───────────────────────────────

  const groupByRole = (items) => {
    const groups = {};
    (items || []).forEach((item) => {
      const role = item.role_type || 'Other';
      if (!groups[role]) groups[role] = { assigneeId: item.assignee_id, items: [] };
      groups[role].items.push(item);
      if (!groups[role].assigneeId && item.assignee_id) {
        groups[role].assigneeId = item.assignee_id;
      }
    });
    return groups;
  };

  // ─── Derived data ──────────────────────────

  const activeDeliverable = deliverables.find((d) => d.id === activeTabId);
  const roleGroups = activeDeliverable ? groupByRole(activeDeliverable.deliverable_items) : {};

  // Panel data
  const panelData = useMemo(() => {
    if (!panelRole) return null;
    const d = deliverables.find((x) => x.id === panelRole.deliverableId);
    if (!d) return null;
    const items = (d.deliverable_items || []).filter((i) => i.role_type === panelRole.roleType);
    const group = groupByRole(d.deliverable_items)[panelRole.roleType];
    return {
      deliverableId: d.id,
      deliverableTitle: d.title,
      roleType: panelRole.roleType,
      assigneeId: group?.assigneeId || null,
      items,
    };
  }, [panelRole, deliverables]);

  // ─── Render ────────────────────────────────

  return (
    <div className="deliverable-list-v2">

      <DeliverableSidebar
        deliverables={deliverables}
        activeTabId={activeTabId}
        onSelectTab={setActiveTabId}
        onDeleteDeliverable={handleDeleteDeliverable}
        onEditTitleSave={handleEditTitleSave}
        onAddDeliverable={handleAddDeliverable}
        onMoveDeliverable={handleMoveDeliverable}
      />

      <DeliverableContent
        deliverable={activeDeliverable}
        roleGroups={roleGroups}
        usersMap={usersMap}
        onQaToggle={handleQaToggle}
        onOpenPanel={(deliverableId, roleType) => setPanelRole({ deliverableId, roleType })}
        onAddRoleSection={handleAddRoleSection}
        onDeleteRole={handleDeleteRole}
        onDescriptionChange={handleDescriptionChange}
        onHandoff={handleHandoff}
      />

      {panelData && (
        <RoleItemPanel
          {...panelData}
          usersMap={usersMap}
          companyUsers={companyUsers || []}
          onClose={() => setPanelRole(null)}
          onToggleCompleted={handleToggleCompleted}
          onItemTitleBlur={handleItemTitleBlur}
          onItemHoursBlur={handleItemHoursBlur}
          onItemActualHoursBlur={handleItemActualHoursBlur}
          onItemNotesBlur={handleItemNotesBlur}
          onDeleteItem={handleDeleteItem}
          onAddItem={handleAddItem}
          onAssigneeChange={handleAssigneeChange}
          onRenameRole={handleRenameRole}
          existingRoles={Object.keys(roleGroups)}
        />
      )}

      {deliverables.length === 0 && (
        <div className="dl-empty-v2">
          <div className="dl-empty-icon">
            <svg width="48" height="48" viewBox="0 0 48 48" fill="none" stroke="#ccc" strokeWidth="1.5">
              <rect x="8" y="12" width="32" height="8" rx="2" />
              <rect x="8" y="24" width="32" height="8" rx="2" />
              <rect x="8" y="36" width="20" height="8" rx="2" strokeDasharray="4 2" />
            </svg>
          </div>
          <p className="dl-empty-title">尚無交付項目</p>
          <p className="dl-empty-desc">從左側新增第一個交付項目開始規劃</p>
        </div>
      )}
    </div>
  );
};

export default DeliverableList;
